import React from 'react';
import settingFieldConfigs from './settingFieldConfigs.ts';
import SettingProcessorConfigField, { type ValueFormat } from './SettingProcessorConfigField';
import { TimeZone } from '@utils/date';

type ValueType = keyof typeof settingFieldConfigs;

const PROCESSOR_CONFIG_SLUGS = ['processor_merchant_id'];

type RenderSettingFieldFromConfigProps = {
  valueType: ValueType;
  value: string | number | boolean | object | null;
  onChange: (e: { target: { value: string | number | boolean | object } }) => void;
  label?: string;
  timezone?: string;
  slug?: string;
  [key: string]: unknown;
};

export function RenderSettingFieldFromConfig({
  valueType,
  value,
  onChange,
  label,
  timezone = TimeZone.RIGA,
  slug,
  ...rest
}: RenderSettingFieldFromConfigProps) {
  const config = settingFieldConfigs[valueType] || settingFieldConfigs.string;
  const Component = config.component as React.ElementType;
  switch (valueType) {
    case 'boolean':
      return (
        <Component
          value={value === 'true'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ target: { value: e.target.checked ? 'true' : 'false' } })
          }
          {...rest}
          label={label || config.label}
        />
      );
    case 'date':
      return (
        <Component
          value={value ? new Date(value as string) : null}
          onChange={(date: Date | null) =>
            onChange({ target: { value: date ? date.toISOString() : '' } })
          }
          {...rest}
          label={label || config.label}
          timezone={timezone}
        />
      );
    case 'array':
      return (
        <Component
          value={value}
          onChange={(arr: string[]) => onChange({ target: { value: arr } })}
          {...rest}
          label={label || config.label}
        />
      );
    case 'json':
      if (slug && PROCESSOR_CONFIG_SLUGS.includes(slug)) {
        return (
          <SettingProcessorConfigField
            value={typeof value === 'object' && value !== null ? (value as ValueFormat) : {}}
            onChange={onChange}
            label={label}
          />
        );
      }
      return (
        <Component
          value={
            typeof value === 'object' && value !== null
              ? JSON.stringify(value, null, 2)
              : (value ?? '')
          }
          onChange={onChange}
          {...rest}
          label={label || config.label}
        />
      );
    case 'string':
    case 'number':
    default:
      return (
        <Component value={value} onChange={onChange} {...rest} label={label || config.label} />
      );
  }
}
